import React, { useState, useEffect } from 'react';

interface FacilityData {
    pickupPointName: string;
    address: string;
    images: string[];
    description: string;
}

interface ImpactHubCarouselProps {
    facilities: FacilityData[];
    onShowAll?: () => void;
}

const AutoScrollingImages: React.FC<{ images: string[] }> = ({ images }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Use dummy images if no images provided
    const dummyImages = [
        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop'
    ];

    const imagesToUse = images && images.length > 0 ? images : dummyImages;

    useEffect(() => {
        if (imagesToUse.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === imagesToUse.length - 1 ? 0 : prevIndex + 1
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [imagesToUse.length]);

    return (
        <div className="relative w-full h-56 overflow-hidden rounded-lg">
            <div
                className="flex transition-transform duration-1000 ease-in-out h-full"
                style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
                {imagesToUse.map((image, index) => (
                    <div key={index} className="w-full h-full flex-shrink-0">
                        <img
                            src={image}
                            alt={`Facility image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = dummyImages[index % dummyImages.length];
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>

            {/* Image indicators */}
            {imagesToUse.length > 1 && (
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {imagesToUse.map((_, index) => (
                        <div
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'bg-white shadow-lg' : 'bg-white/60'
                                }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const ImpactHubCarousel: React.FC<ImpactHubCarouselProps> = ({ facilities, onShowAll }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const cardsPerView = 4;

    // Dummy data if no facilities provided
    const dummyFacilities = [
        {
            pickupPointName: "Green valley recycling center",
            address: "123 Environmental Way, Green City, EC 12345",
            images: [
                'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop'
            ],
            description: "A state-of-the-art recycling facility that processes over 500 tons of waste monthly, contributing significantly to environmental sustainability and community welfare."
        },
        {
            pickupPointName: "Blue ocean waste management",
            address: "456 Ocean Drive, Coastal Town, CT 67890",
            images: [
                'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop'
            ],
            description: "Specializing in marine waste collection and plastic recycling, helping to keep our oceans clean and healthy for future generations."
        },
        {
            pickupPointName: "Urban sustainability hub",
            address: "789 City Center, Metropolitan Area, MA 11111",
            images: [
                'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=300&fit=crop'
            ],
            description: "Community-focused facility providing education and waste processing services for urban residents, promoting sustainable living practices."
        },
        {
            pickupPointName: "EcoTech processing plant",
            address: "321 Innovation Boulevard, Tech Valley, TV 22222",
            images: [
                'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop'
            ],
            description: "Advanced technology-driven facility using AI and automation for efficient waste sorting and recycling processes with maximum environmental impact."
        },
        {
            pickupPointName: "Community green initiative",
            address: "654 Community Street, Neighborhood, NB 33333",
            images: [
                'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=300&fit=crop',
                'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop'
            ],
            description: "Grassroots organization focused on local waste reduction and community education programs, empowering citizens to make a difference."
        }
    ];

    const facilitiesToUse = facilities && facilities.length > 0 ? facilities : dummyFacilities;

    const nextSlide = () => {
        const maxIndex = Math.max(0, facilitiesToUse.length - cardsPerView);
        setCurrentIndex((prevIndex) =>
            prevIndex >= maxIndex ? 0 : prevIndex + 1
        );
    };

    const prevSlide = () => {
        const maxIndex = Math.max(0, facilitiesToUse.length - cardsPerView);
        setCurrentIndex((prevIndex) =>
            prevIndex <= 0 ? maxIndex : prevIndex - 1
        );
    };

    if (!facilitiesToUse || facilitiesToUse.length === 0) {
        return (
            <div className="w-full">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    {/* <span className='card-label fw-bold fs-3 mb-1'>Featured Hubs</span> */}
                    {/* <h2 className="text-xl font-bold text-gray-900">Impact Hubs</h2> */}
                    {onShowAll && (
                        <button
                            onClick={onShowAll}
                            className='btn btn-primary fw-bold px-6'
                        // className="font-medium text-sm transition-colors duration-200"
                        // style={{ background: "#ed6e43" }}
                        >
                            Show All
                        </button>
                    )}
                </div>

                <div className="flex items-center justify-center p-12 text-gray-500 bg-gray-50 rounded-lg">
                    No facilities to display
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl p-6 shadow-lg" style={{ margin: "20px 0" }}>
            {/* Header */}
            <div className="w-full bg-white flex justify-between items-center" style={{ margin: "5px 0", padding: "5px 10px" }}>
                <span className='card-label fw-bold fs-3 mb-1'>Featured news</span>
                {onShowAll && (
                    <button
                        onClick={onShowAll}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full font-medium text-sm transition-all duration-200 hover:shadow-lg transform hover:scale-105"
                    >
                        Show All
                    </button>
                )}
            </div>

            {/* Carousel Container */}
            <div className="relative">
                {/* Cards Container */}
                <div className="overflow-hidden">
                    <div
                        className="flex transition-transform duration-500 ease-in-out gap-6"
                        style={{
                            transform: `translateX(-${currentIndex * (100 / cardsPerView)}%)`,
                        }}
                    >
                        {facilitiesToUse.map((facility, index) => (
                            <div
                                key={index}
                                className="w-full md:w-1/2 lg:w-1/4 flex-shrink-0"
                            >
                                <div className="bg-white rounded-2xl shadow-xl overflow-hidden h-full hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-gray-100">
                                    {/* Content Header */}
                                    <div className="p-6 pb-4">
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                                            {facility.pickupPointName}
                                        </h3>

                                        {/* Address */}
                                        <div className="flex items-start mb-4">
                                            {/* <div className="w-4 h-4 text-gray-500 mt-0.5 mr-2">
                                                <svg fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                </svg>
                                            </div> */}
                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                {facility.address}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Auto-scrolling Images */}
                                    <div className="px-6 mb-4">
                                        <AutoScrollingImages images={facility.images} />
                                    </div>

                                    {/* Description */}
                                    <div className="p-6 pt-2">
                                        <div className="border-t border-gray-100 pt-4">
                                            <p className="text-gray-800 text-sm leading-relaxed">
                                                {facility.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Navigation Arrows */}
                {facilitiesToUse.length > cardsPerView && (
                    <>
                        <button
                            onClick={prevSlide}
                            className="absolute -left-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-50 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-20 border border-gray-200"
                            aria-label="Previous facilities"
                        >
                            <div className="w-6 h-6 flex items-center justify-center">
                                <span className="text-gray-700 text-2xl font-bold">‹</span>
                            </div>
                        </button>

                        <button
                            onClick={nextSlide}
                            className="absolute -right-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-50 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-20 border border-gray-200"
                            aria-label="Next facilities"
                        >
                            <div className="w-6 h-6 flex items-center justify-center">
                                <span className="text-gray-700 text-2xl font-bold">›</span>
                            </div>
                        </button>
                    </>
                )}

                {/* Dots Indicator */}
                {facilitiesToUse.length > cardsPerView && (
                    <div className="flex justify-center mt-8 space-x-3">
                        {Array.from({ length: Math.ceil(facilitiesToUse.length / cardsPerView) }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                className={`w-4 h-4 rounded-full transition-all duration-300 ${Math.floor(currentIndex / cardsPerView) === index
                                    ? 'bg-blue-600 scale-125 shadow-lg'
                                    : 'bg-gray-300 hover:bg-gray-400 hover:scale-110'
                                    }`}
                                aria-label={`Go to slide set ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImpactHubCarousel;